<script lang="ts" setup>
import { ref, Ref, computed, watch, onMounted, nextTick } from 'vue';
import store from '@/store';
import { useI18n } from 'vue-i18n';
import {
    MultiSelectOption,
    NumberActions,
    NumberFilter,
    NumberGetters,
    SelectOptionsServiceGetCitiesResult,
} from '@/types';
import { FilterIcon } from '@heroicons/vue/solid';
import { toastServiceError } from '@/services/core/notification';
import SelectOptionsService from '@/services/api/SelectOptionsService';

const { t } = useI18n();

const filtersOpened = ref(false);
const suppressCityReset = ref(false);

const storeFilter = computed(() => store.getters[NumberGetters.FILTER]);
const defaultFilter = computed(() => store.getters[NumberGetters.DEFAULT_FILTER]);

const filter: Ref<NumberFilter> = ref({
    hasConnectFlow: defaultFilter.value.hasConnectFlow,
    state: null,
    city: null,
});

const hasConnectFlowFilterOptions: Ref<MultiSelectOption[]> = ref([
    {
        label: t('number.filters.hasConnectFlow.options.yes'),
        value: true,
    } as MultiSelectOption,
    {
        label: t('number.filters.hasConnectFlow.options.no'),
        value: false,
    } as MultiSelectOption,
]);

const cities: Ref<SelectOptionsServiceGetCitiesResult[]> = ref([]);
const getCities = async () => {
    try {
        const response = await SelectOptionsService.getCities();
        cities.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const stateOptions = computed(() =>
    [...new Set(cities.value.map((c) => c.state))].sort().map((state) => ({
        state,
    })),
);
const cityOptions = computed(() =>
    filter.value.state ? cities.value.filter((c) => c.state === filter.value.state) : cities.value,
);

function applyFilters() {
    store.dispatch(NumberActions.SET_FILTER, filter.value);
}

function clearFilters() {
    store.dispatch(NumberActions.CLEAR_FILTER);
    refreshLocalFilter();
}

function refreshLocalFilter() {
    suppressCityReset.value = true;
    filter.value.hasConnectFlow = storeFilter.value.hasConnectFlow;
    filter.value.state = storeFilter.value.state;
    filter.value.city = storeFilter.value.city;
    nextTick(() => {
        suppressCityReset.value = false;
    });
}

watch(
    () => filter.value.state ?? null,
    (nv, ov) => {
        if (nv !== ov && !suppressCityReset.value) {
            filter.value.city = null;
        }
    },
);

watch(filtersOpened, () => {
    if (filtersOpened.value) refreshLocalFilter();
});

onMounted(() => {
    getCities();
});
</script>

<template>
    <UiRightPanel v-model="filtersOpened" :title="$t('filter.sidebar.title')">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                class="hover:bg-gray-50"
                @click="filtersOpened = true"
            >
                <FilterIcon class="mr-0 h-5 w-5 text-gray-400 sm:mr-3" aria-hidden="true" />
                <span class="hidden sm:block">{{ $t('core.actions.Filter') }}</span>
            </UiButton>
        </template>
        <template #default>
            <form autocomplete="off" class="flex flex-col gap-3">
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('number.filters.hasConnectFlow.label') }}
                        </label>
                        <UiMultiselect
                            name="filters_hasConnectFlow"
                            v-model="filter.hasConnectFlow"
                            :options="hasConnectFlowFilterOptions"
                            can-clear
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('number.filters.state.label') }}
                        </label>
                        <UiMultiselect
                            name="filters_state"
                            v-model="filter.state"
                            :options="stateOptions"
                            label="state"
                            value-prop="state"
                            can-clear
                            primitive
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6">
                    <div class="sm:col-span-4">
                        <label class="block text-sm font-medium text-gray-800">
                            {{ $t('number.filters.city.label') }}
                        </label>
                        <UiMultiselect
                            name="filters_city"
                            v-model="filter.city"
                            :options="cityOptions"
                            label="city"
                            value-prop="city"
                            can-clear
                            primitive
                            :placeholder="t('core.options.All')"
                        />
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-2 place-content-around gap-4 text-center">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters()">
                        {{ $t('core.actions.Clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="applyFilters()">
                        {{ $t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>
